import { Route } from 'vue-router';
import { Jurisdiction } from '@gig/magic-jurisdictions';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { getUserJurisdiction } from './jurisdiction.helpers';
import { IP_COUNTRY_QUERY } from '@gig/magic-messages';

/**
 * Configuration that specifies if access to route should be allowed while user verification is pending.
 * `allowedPathsPerCountry`:
 *      specifies the allowed paths for each country.
 *      The ip country of the unverified user will be used to determine the allowed paths.
 *   `allowedPathsPerJurisdiction`:
 *      specifies the allowed paths for each jurisdiction.
 *      The country of the unverified user will be used to determine the allowed paths.
 * `extractPathFromRoute`:
 *      function that returns the path of that route,
 *      which will determine if access for that user is allowed or not.
 */
interface IConditions {
    allowedJurisdictions?: Jurisdiction[];
    allowedPathsPerCountry?: {
        [key in Country]?: string[];
    };
    allowedPathsPerJurisdiction?: {
        [key in Jurisdiction]?: string[];
    };
    extractPathFromRoute?: (route: Route) => string;
}

/**
 * This function adds the metadata about the access that an unverified user should have.
 * Depending on the user's ip country, as well as the config provided, the possible return values are:
 * - `pendingVerificationAllowed`:
 *          boolean, shows if the user has access or not to this route
 * OR
 * - `extractPathFromRoute` and `pathsAllowedPendingVerification`:
 *          paths of that specific route that are allowed, and a function to extract
 *          the path from the route (e.g. the cashier route has multiple views/paths)
 *
 * @param conditions see `IConditions` for details
 */
export function allowPendingVerification() {
    return { pendingVerificationAllowed: true };
}

export async function allowPendingVerificationByConditions(
    conditions: IConditions,
): Promise<{
    pendingVerificationAllowed?: boolean;

    extractPathFromRoute?: (route: Route) => string;
    pathsAllowedPendingVerification?: string[];
}> {
    return (
        (await getCountryPathConfiguration(conditions)) ||
        (await getJurisdictionPathConfiguration(conditions)) ||
        (await getJurisdictionConfiguration(conditions)) || {
            pendingVerificationAllowed: false,
        }
    );
}

async function getCountryPathConfiguration(conditions: IConditions) {
    const ipCountry = await window.queryBus.query<Country>(IP_COUNTRY_QUERY)!;
    if (conditions.allowedPathsPerCountry?.hasOwnProperty(ipCountry!)) {
        return {
            extractPathFromRoute: conditions.extractPathFromRoute,
            pathsAllowedPendingVerification:
                conditions.allowedPathsPerCountry[ipCountry],
        };
    }

    return null;
}

async function getJurisdictionPathConfiguration(conditions: IConditions) {
    const userJurisdiction = await getUserJurisdiction();

    if (
        conditions.allowedPathsPerJurisdiction?.hasOwnProperty(userJurisdiction)
    ) {
        return {
            extractPathFromRoute: conditions.extractPathFromRoute,
            pathsAllowedPendingVerification:
                conditions.allowedPathsPerJurisdiction[userJurisdiction],
        };
    }

    return null;
}

async function getJurisdictionConfiguration(conditions: IConditions) {
    const userJurisdiction = await getUserJurisdiction();

    if (conditions.allowedJurisdictions?.includes(userJurisdiction)) {
        return {
            pendingVerificationAllowed: true,
        };
    }

    return null;
}
